import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Input } from '../../../../../components/ui/Input';
import { useFocusTrap } from '../../../../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../../../../app/hooks/useEscapeKey';
interface SaveSnapshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (label: string, note: string) => void;
}

/**
 * Deze dialoog was een kale overlay: geen rol, geen focusvang, geen Escape.
 * Met het toetsenbord tabde je er dwars doorheen naar de pagina erachter — en
 * die pagina staat vol met knoppen die de pipeline opnieuw laten draaien. De
 * rest van de codebase gebruikt hiervoor consequent `useFocusTrap` +
 * `useEscapeKey`; dit bestand was daarin achtergebleven.
 */
export function SaveSnapshotModal({
  isOpen,
  onClose,
  onSave
}: SaveSnapshotModalProps) {
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const trapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  if (!isOpen) return null;
  const handleSave = () => {
    onSave(label, note);
    setLabel('');
    setNote('');
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
  // Klik buiten de dialoog sluit hem. Bewust alleen op de achtergrond
  // zelf, niet op alles wat erin bubbelt — anders sluit een klik in een
  // tekstveld de dialoog.
  onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-5811" data-mp="1hdm7p3308vxf">
      
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby="snapshot-modal-title" className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-5812" data-mp="tq4ohvxt3662">
        
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-5813" data-mp="13nsj8lvewdo8">
          <h3 id="snapshot-modal-title" className="font-semibold text-gray-900" data-id="element-5814" data-mp="2a21l5l6hn66n">
            
            Snapshot opslaan
          </h3>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-5815" data-mp="ezeos83amlx1">
            
            <X className="w-5 h-5" aria-hidden="true" data-id="element-5816" data-mp="nys1raqmlsmi" />
          </button>
        </div>
        <div className="p-4 space-y-4" data-id="element-5817" data-mp="jzj805vl5uqy">
          <div data-id="element-5818" data-mp="jwde8dc0i4ow">
            <label htmlFor="snapshot-label" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5819" data-mp="3seb0lfnsyp8v">
              
              Label (optioneel)
            </label>
            <Input id="snapshot-label" value={label} onChange={e => setLabel(e.target.value)} placeholder="Bijv. 'Voor T2.2 fix'" className="w-full" data-id="element-5820" data-mp="3nh1by288mhm3" />
            
          </div>
          <div data-id="element-5821" data-mp="9p9l8yd8hdr">
            <label htmlFor="snapshot-note" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5822" data-mp="97eifnfw97f">
              
              Notitie (optioneel)
            </label>
            <textarea id="snapshot-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Extra context over deze run..." className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 min-h-[80px]" data-id="element-5823" data-mp="34nnqcnqeznj5" />
            
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 bg-gray-50" data-id="element-5824" data-mp="2kkhts4wrys0o">
          <Button variant="ghost" onClick={onClose} data-id="element-5825" data-mp="2pjcitolc5ct8">
            Annuleren
          </Button>
          <Button variant="primary" onClick={handleSave} data-id="element-5826" data-mp="3d96tbzi6mpab">
            Opslaan
          </Button>
        </div>
      </div>
    </div>;
}